import { useState, useEffect, useCallback, useRef } from 'react';
import { backendClient } from '@/lib/backendClient';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from './useWebSocket';
import { apiClient } from '@/utils/apiClient';

export interface JobStatus {
  id: string;
  status: 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'service_unavailable';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  job_type: string;
  progress: number;
  queue_position?: number;
  estimated_completion?: string;
  created_at: string;
  completed_at?: string;
  error_message?: string;
  result_data?: any;
  retry_count?: number; // Number of retry attempts for DLQ jobs
}

interface UseJobStatusOptions {
  jobId: string | null;
  enabled?: boolean;
  pollInterval?: number; // milliseconds
  useWebSocket?: boolean; // Enable WebSocket for real-time updates
  onCompleted?: (result: any) => void;
  onError?: (error: string) => void;
}

/**
 * Hook to poll job status from backend
 * Supports both HTTP polling and WebSocket for real-time updates
 */
export const useJobStatus = ({
  jobId,
  enabled = true,
  pollInterval = 2000,
  useWebSocket: enableWebSocket = true,
  onCompleted,
  onError
}: UseJobStatusOptions) => {
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Stable refs for callbacks to prevent useEffect re-triggering
  const onCompletedRef = useRef(onCompleted);
  const onErrorRef = useRef(onError);
  onCompletedRef.current = onCompleted;
  onErrorRef.current = onError;

  /**
   * WebSocket message handler
   */
  const handleWebSocketMessage = useCallback((message: any) => {
    // Only process job_update messages for our job
    if (message.type === 'job_update' && message.job_id === jobId) {
      console.log('📨 Received WebSocket job update:', message.data);

      // Update job status from WebSocket data
      setJobStatus(prev => ({
        ...prev,
        id: jobId!,
        status: message.data.status,
        progress: message.data.progress || 0,
        queue_position: message.data.queue_position,
        estimated_completion: message.data.estimated_completion,
        error_message: message.data.message || prev?.error_message,
        result_data: message.data.result || prev?.result_data
      } as JobStatus));

      // Handle completion
      if (message.data.status === 'completed' && onCompletedRef.current) {
        onCompletedRef.current(message.data.result);
      }

      // Handle error
      if (message.data.status === 'failed' && onErrorRef.current) {
        onErrorRef.current(message.data.message || 'Job failed');
      }
    }
  }, [jobId]);

  /**
   * WebSocket connection
   */
  const { isConnected: wsConnected } = useWebSocket({
    enabled: enableWebSocket && enabled && !!jobId,
    onMessage: handleWebSocketMessage
  });

  /**
   * Fetch job status from backend
   */
  const fetchJobStatus = useCallback(async () => {
    if (!jobId || !user || !enabled) return;

    try {
      setLoading(true);
      setError(null);

      // Call backend job status endpoint using apiClient
      const isBackendMode = import.meta.env.VITE_USE_BACKEND === 'true';

      let status: JobStatus;

      if (isBackendMode) {
        // Use apiClient for backend mode (handles cookies)
        status = await apiClient.get<JobStatus>(`/jobs/job/${jobId}`);
      } else {
        // Fallback for Supabase mode
        const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/jobs/job/${jobId}`, {
          headers: {
            'Authorization': `Bearer ${await backendClient.auth.getSession().then(s => s.data.session?.access_token)}`,
            'Content-Type': 'application/json'
          },
          credentials: 'include' // Include cookies for authentication
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch job status: ${response.statusText}`);
        }

        status = await response.json();
      }
      setJobStatus(status);

      // Handle completed state
      if (status.status === 'completed') {
        if (onCompletedRef.current) {
          onCompletedRef.current(status.result_data);
        }
        // Stop polling on completion
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }

      // Handle error state
      if (status.status === 'failed') {
        const errorMsg = status.error_message || 'Job failed';
        setError(errorMsg);
        if (onErrorRef.current) {
          onErrorRef.current(errorMsg);
        }
        // Stop polling on failure
        if (pollingRef.current) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch job status';
      console.error('Job status fetch error:', errorMsg);
      // Don't set error state or call onError for network/fetch errors
      // These are transient and polling will retry automatically
      // Only actual job failures (status === 'failed') should trigger onError
      console.warn('⚠️ Network error during job status poll - will retry on next poll interval');
    } finally {
      setLoading(false);
    }
  }, [jobId, user, enabled]);

  /**
   * Start polling for job status
   * Use less frequent polling when WebSocket is connected
   */
  useEffect(() => {
    if (!jobId || !enabled) {
      // Clear any existing polling
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    // Initial fetch
    fetchJobStatus();

    // Use longer interval when WebSocket is connected (fallback polling)
    const actualPollInterval = wsConnected ? pollInterval * 5 : pollInterval;

    // Start polling
    pollingRef.current = setInterval(() => {
      fetchJobStatus();
    }, actualPollInterval);

    // Cleanup
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [jobId, enabled, pollInterval, wsConnected, fetchJobStatus]);

  /**
   * Calculate estimated time remaining
   */
  const getEstimatedTimeRemaining = useCallback((): string | null => {
    if (!jobStatus?.estimated_completion) return null;

    const now = new Date();
    const estimatedCompletion = new Date(jobStatus.estimated_completion);
    const diffMs = estimatedCompletion.getTime() - now.getTime();

    if (diffMs <= 0) return 'Any moment now...';

    const diffSeconds = Math.floor(diffMs / 1000);
    const diffMinutes = Math.floor(diffSeconds / 60);

    if (diffMinutes > 0) {
      return `~${diffMinutes} min remaining`;
    } else {
      return `~${diffSeconds} sec remaining`;
    }
  }, [jobStatus]);

  /**
   * Get user-friendly status message
   */
  const getStatusMessage = useCallback((): string => {
    if (!jobStatus) return 'Waiting...';

    switch (jobStatus.status) {
      case 'pending':
      case 'queued':
        return jobStatus.queue_position
          ? `Position in queue: #${jobStatus.queue_position}`
          : 'Waiting in queue...';
      case 'processing':
        return `Processing... ${Math.round(jobStatus.progress * 100)}%`;
      case 'completed':
        return 'Completed!';
      case 'failed':
        return jobStatus.error_message || 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'service_unavailable':
        const retryInfo = jobStatus.retry_count ? ` (retry ${jobStatus.retry_count}/5)` : '';
        return `Queued for retry${retryInfo}...`;
      default:
        return 'Unknown status';
    }
  }, [jobStatus]);

  return {
    jobStatus,
    loading,
    error,
    isQueued: jobStatus?.status === 'pending' || jobStatus?.status === 'queued',
    isProcessing: jobStatus?.status === 'processing',
    isCompleted: jobStatus?.status === 'completed',
    isFailed: jobStatus?.status === 'failed',
    queuePosition: jobStatus?.queue_position,
    progress: jobStatus?.progress || 0,
    estimatedTimeRemaining: getEstimatedTimeRemaining(),
    statusMessage: getStatusMessage(),
    refetch: fetchJobStatus
  };
};
